import { STOCK_WARNING_LIMIT } from '@constants'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Gift } from '@pets-ui/Icons'
import { RemindCircle } from '@pets-ui/icons'
import { Image } from '@pets-ui/Image/Image'
import { NumberInput, type NumberInputProps } from '@pets-ui/NumberInput/NumberInput'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'

const StyledText = ({ children }: { children: ReactNode }) => {
  return <Text variant="caption" color={colors.red400}>{children}</Text>
}
const styles = stylex.create({
  imageRoot: {
    borderColor: colors.grey200,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: '1px',
  },
  attributeText: {
    padding: 4,
    borderRadius: 4,
    backgroundColor: colors.grey100,
    minWidth: 120,
  },
  selectedAnimation: {
    transition: 'background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  selected: {
    backgroundColor: colors.yellow100,
  },
  remindCircle: {
    color: colors.green600,
    fontSize: 20,
    opacity: 0,
    position: 'absolute',
    zIndex: 1,
    left: 0,
    top: 0,
  },
  remindCircleAnimation: {
    transition: 'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: 1,
  },
  notSelected: {
    backgroundColor: colors.grey100,
  },
  notSelectedImage: {
    opacity: 0.6,
  },
})

interface InputContentProps extends NumberInputProps {
  isPrivate: boolean
  stock: number
  value: number | undefined
  max: number
  isReadOnly: boolean
}
const InputContent = (props: InputContentProps) => {
  const { isPrivate, stock, value, max, isReadOnly, ...rest } = props
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  if (isPrivate) return <StyledText>{t('cartUi.notAvailable', { defaultValue: '已無法提供' })}</StyledText>
  if (stock === 0) return <StyledText>{t('cartUi.giftsOutOfStock', { defaultValue: '贈品已贈完' })}</StyledText>

  if (isReadOnly) {
    return (
      <Text variant="caption" color={colors.grey500}>
        x
        {value}
      </Text>
    )
  }

  return (
    <NumberInput
      animated
      color="white"
      value={value}
      max={max}
      ButtonDecrementProps={{
        'data-testid': 'giveaway-number-input-decrement-button',
      }}
      ButtonIncrementProps={{
        'data-testid': 'giveaway-number-input-increment-button',
      }}
      {...rest}
    />
  )
}

export interface GiveawayNumberInputProps extends NumberInputProps {
  thumbnail?: string
  stock: number
  isPrivate?: boolean
  name?: string
  attributes?: Record<string, string>
  notSelected?: boolean
  isReadOnly?: boolean
}

export const GiveawayNumberInput = (props: GiveawayNumberInputProps) => {
  const { thumbnail, isPrivate, value, max, stock, name, attributes = {}, notSelected, isReadOnly, ...rest } = props
  const attributeText = Object.values(attributes).join(', ')
  const isSelected = !!value || (isReadOnly && stock > 0)
  const isSelectedAllStock = stock > 0 && ((value || 0) === stock)
  const shouldShowStockWarning = stock > 0 && stock < STOCK_WARNING_LIMIT
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  return (
    <HStack
      alignItems="center"
      gap={2}
      borderWidth={1}
      borderColor={colors.grey300}
      borderStyle="solid"
      borderRadius={4}
      p="8px 12px 8px 8px"
      position="relative"
      rootStyle={[
        styles.selectedAnimation,
        isSelected ? styles.selected : undefined,
        notSelected ? styles.notSelected : undefined,
      ]}
    >
      <RemindCircle rootStyle={[styles.remindCircle, isSelected ? styles.remindCircleAnimation : undefined]} />
      <Image
        src={thumbnail}
        fallback={<Gift fontSize={52} />}
        width={52}
        height={52}
        xStyles={{
          imgBase: {
            root: [styles.imageRoot, notSelected ? styles.notSelectedImage : undefined],
          },
        }}
      />
      <VStack flex={1} gap={1} alignItems="flex-start">
        <Text lineClamp={1} color={notSelected ? colors.grey600 : colors.grey800}>{name}</Text>
        {attributeText && (
          <Box rootStyle={styles.attributeText}>
            <Text lineClamp={1} color={notSelected ? colors.grey400 : colors.grey800} variant="caption">{attributeText}</Text>
          </Box>
        )}
      </VStack>
      <VStack alignItems="flex-end" gap={1}>
        <InputContent isPrivate={!!isPrivate} stock={stock} value={value} max={max} isReadOnly={!!isReadOnly} {...rest} />
        {!isReadOnly && shouldShowStockWarning && !isSelectedAllStock && (
          <Text variant="caption" color={colors.red600}>
            {t('cartUi.remainQuantity', { defaultValue: '剩餘數量' })}
            ：
            {stock - (value || 0)}
          </Text>
        )}
        {!isReadOnly && isSelectedAllStock && (
          <Text variant="caption" color={colors.red600}>
            {t('cartUi.isSelectAllStock', { defaultValue: '已達庫存上限' })}
          </Text>
        )}
      </VStack>
    </HStack>
  )
}
